import { Permission } from 'constants/permissions'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { updateBearerToken } from 'utils/api'
import { AuthHook, User } from '../types'

const USER_EMAIL = 'nobody@deepcellbio.com'

// Hardcoded on-prem token: base64 of {"token_type": "user"}
// This matches the implementation in cell_data_api/on_prem/auth_utils.py
const ONPREM_TOKEN = btoa(JSON.stringify({ token_type: 'user' }))

// Permissions that on-prem users should NOT have
const RESTRICTED_PERMISSIONS: Set<Permission> = new Set([
  'deepcell_internal:read',
  'qc:edit',
  'qc:approve',
] as Permission[])

/**
 * useOnPremAuth
 *
 * Hook for on-premises authentication.
 * Uses a hardcoded token that matches the on-prem API expectations.
 */
export default function useOnPremAuth(): AuthHook {
  const [tokenInitialized, setTokenInitialized] = useState(false)

  // Initialize the token on mount
  useEffect(() => {
    updateBearerToken(ONPREM_TOKEN)
    setTokenInitialized(true)
  }, [])

  const hasPermission = useCallback((permission: Permission): boolean => {
    // On-prem users have all permissions except those in the restricted list
    return !RESTRICTED_PERMISSIONS.has(permission)
  }, [])

  const loginWithRedirect = useCallback(async (): Promise<void> => {
    // No-op: already authenticated in on-prem mode
    return Promise.resolve()
  }, [])

  const logout = useCallback((): void => {
    // No-op: can't logout in on-prem mode
  }, [])

  const user: User = useMemo(
    () => ({
      email: USER_EMAIL,
      name: 'On-Prem User',
    }),
    []
  )

  return useMemo(
    () => ({
      isAuthenticated: true,
      isLoading: !tokenInitialized,
      user,
      error: undefined,
      isInternalUser: false,
      supportsLogin: false,
      hasPermission,
      loginWithRedirect,
      logout,
    }),
    [tokenInitialized, user, hasPermission, loginWithRedirect, logout]
  )
}
